import { useCallback, useMemo, useRef, useState, type RefObject } from "react";
import {
  toChatSourcePosition,
  useExperienceDocuments,
  type ExperienceDocument,
  type PdfViewerHandle,
} from "@libertify/experience-viewer";
import { UNUSED_SOURCE_COLOR } from "./constants";
import { rowMarker } from "./helpers";
import type {
  ProjectDocument,
  ShownMark,
  StandoutHighlight,
  StandoutRow,
} from "./types";

/**
 * The PDF pane's document and its one standout mark. Every `show` draws under
 * a fresh id: the viewer animates an id only the first time it sees it, so
 * reusing one would redraw a re-click without replaying it.
 */
export const useStandoutHighlight = (
  projectDocuments: ProjectDocument[],
  viewerRef: RefObject<PdfViewerHandle>
): StandoutHighlight => {
  // Only the main document: the first visible one, which is the aggregated
  // "All documents" one for multi-document projects.
  const documents = useMemo<ExperienceDocument[]>(() => {
    const main = projectDocuments.find((d) => Boolean(d.path) && !d.hidden);
    return main
      ? [
          {
            id: main.id,
            url: main.path,
            name: main.name,
            pageCount: main.nb_of_pages,
          },
        ]
      : [];
  }, [projectDocuments]);

  const controller = useExperienceDocuments({
    documents,
    pdfViewerRef: viewerRef,
    sourceHighlightColor: UNUSED_SOURCE_COLOR,
  });
  const { onSourceClick } = controller;

  const [shown, setShown] = useState<ShownMark | null>(null);
  const showCount = useRef(0);

  const show = useCallback(
    (row: StandoutRow) => {
      const [source] = row.standout.sources ?? [];
      if (!source) return;
      showCount.current += 1;
      const id = `${row.id}-${showCount.current}`;
      onSourceClick(
        {
          id,
          documentId: source.document_id,
          quote: source.zone.quote,
          pageNumber: source.zone.position.page_number,
          position: toChatSourcePosition(source.zone.position),
        },
        rowMarker(row)
      );
      // `onSourceClick` names the mark `source-<id>`.
      setShown({ rowId: row.id, highlightId: `source-${id}` });
    },
    [onSourceClick]
  );

  const clear = useCallback(() => setShown(null), []);

  // The controller keeps every past mark; only the latest is passed on.
  const highlightId = shown?.highlightId;
  const highlights = useMemo(
    () => controller.highlights.filter((h) => h.id === highlightId),
    [controller.highlights, highlightId]
  );
  const cuepoints = useMemo(
    () => controller.cuepoints.filter((c) => c.highlightId === highlightId),
    [controller.cuepoints, highlightId]
  );

  return {
    documents: controller.documents,
    activeDocument: controller.activeDocument,
    activeDocumentId: controller.activeDocumentId,
    highlights,
    cuepoints,
    activeRowId: shown?.rowId,
    show,
    clear,
  };
};
